import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../api/apiService';
import type { User } from '../../types';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Search, UserPlus, UserMinus } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import './CoachTrainees.css';

export const CoachTrainees: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [linkedUsers, setLinkedUsers] = useState<User[]>([]);
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadLinkedUsers();
  }, []);

  const loadLinkedUsers = async () => {
    try {
      const users = await apiService.getCoachTrainees();
      setLinkedUsers(users);
    } catch (error) {
      console.error('Error loading linked users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    if (searchQuery.trim().length < 2) {
      toast.error('Por favor, ingresa al menos 2 caracteres para buscar');
      return;
    }

    setIsSearching(true);
    try {
      const results = await apiService.findUsers(searchQuery);
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching users:', error);
      toast.error('Error al buscar usuarios');
    } finally {
      setIsSearching(false);
    }
  };

  const handleLinkUser = async (userId: number) => {
    try {
      await apiService.linkTrainee(userId);
      await loadLinkedUsers();
      setSearchResults([]);
      setSearchQuery('');
      toast.success('Usuario vinculado correctamente');
    } catch (error: any) {
      console.error('Error linking user:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Error al vincular usuario');
      }
    }
  };

  const handleUnlinkUser = async (userId: number) => {
    if (!confirm('¿Estás seguro de que deseas desvincular este usuario?')) {
      return;
    }

    try {
      await apiService.unlinkTrainee(userId);
      await loadLinkedUsers();
      toast.success('Usuario desvinculado correctamente');
    } catch (error) {
      console.error('Error unlinking user:', error);
      toast.error('Error al desvincular usuario');
    }
  };

  const isUserLinked = (userId: number) => {
    return linkedUsers.some(user => user.id === userId);
  };

  if (isLoading) {
    return <div className="text-center p-4">{t('loading')}</div>;
  }

  return (
    <div className="coach-trainees-page">
      <h1 className="page-title">Mis Alumnos</h1>

      {/* Search Section */}
      <Card className="search-section">
        <h2 className="section-title">Buscar Usuarios</h2>
        <p className="section-description">
          Busca usuarios por nombre, email o nombre de usuario para vincularlos a tu lista de alumnos
        </p>

        <div className="search-bar">
          <Input
            type="text"
            placeholder="Buscar por nombre, email o usuario..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <Button onClick={handleSearch} disabled={isSearching}>
            <Search size={20} />
            {isSearching ? 'Buscando...' : 'Buscar'}
          </Button>
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="search-results">
            <h3 className="results-title">Resultados de búsqueda ({searchResults.length})</h3>
            <div className="users-list">
              {searchResults.map((user) => (
                <div key={user.id} className="user-item">
                  <div className="user-info">
                    <span className="user-name">{user.name}</span>
                    <span className="user-detail">{user.email}</span>
                    <span className="user-detail">@{user.username}</span>
                  </div>
                  <Button
                    onClick={() => handleLinkUser(user.id)}
                    disabled={isUserLinked(user.id)}
                    size="sm"
                  >
                    <UserPlus size={16} />
                    {isUserLinked(user.id) ? 'Ya vinculado' : 'Vincular'}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Linked Users Section */}
      <Card className="linked-users-section">
        <h2 className="section-title">Alumnos Vinculados ({linkedUsers.length})</h2>

        {linkedUsers.length === 0 ? (
          <p className="empty-state">
            Aún no tienes alumnos vinculados. Usa el buscador para encontrar y vincular usuarios.
          </p>
        ) : (
          <div className="users-list">
            {linkedUsers.map((user) => (
              <div key={user.id} className="user-item">
                <div className="user-info">
                  <span className="user-name">{user.name}</span>
                  <span className="user-detail">{user.email}</span>
                  <span className="user-detail">@{user.username}</span>
                </div>
                <Button
                  onClick={() => handleUnlinkUser(user.id)}
                  variant="secondary"
                  size="sm"
                >
                  <UserMinus size={16} />
                  Desvincular
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};
